import { API_BASE_URL } from '../../constants/appConstants';
import { authenticatedRequest } from '../authService';

const SUPERADMIN_BASE_URL = `${API_BASE_URL}/superadmin`;

/**
 * Fetch paginated list of bills with search and filtering
 * @param {Object} params - Query parameters
 * @param {string} params.search - Search term for title, bill_number, vendor, payment_reference
 * @param {number} params.owner - Owner user ID filter
 * @param {number} params.created_by - Created by user ID filter
 * @param {string} params.status - Bill status filter
 * @param {string} params.date_from - Start date filter (YYYY-MM-DD)
 * @param {string} params.date_to - End date filter (YYYY-MM-DD)
 * @param {string} params.sort - Sort order (asc/desc)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.page_size - Items per page (default: 10)
 * @returns {Promise<Object>} Response with bills list and pagination info
 */
export const fetchBills = async (params = {}) => {
  const {
    search = '',
    owner = '',
    created_by = '',
    status = '',
    date_from = '',
    date_to = '',
    sort = 'desc',
    page = 1,
    page_size = 10
  } = params;

  // Build query string
  const queryParams = new URLSearchParams();
  if (search.trim()) queryParams.append('search', search.trim());
  if (owner) queryParams.append('owner', owner.toString());
  if (created_by) queryParams.append('created_by', created_by.toString());
  if (status) queryParams.append('status', status);
  if (date_from) queryParams.append('date_from', date_from);
  if (date_to) queryParams.append('date_to', date_to);
  if (sort) queryParams.append('sort', sort);
  queryParams.append('page', page.toString());
  queryParams.append('page_size', page_size.toString());

  const url = `${SUPERADMIN_BASE_URL}/bills/?${queryParams.toString()}`;

  try {
    const response = await authenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch bills');
    }

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching bills:', error);
    throw new Error(error.message || 'Failed to fetch bills');
  }
};

/**
 * Fetch detailed information for a specific bill
 * @param {number} billId - Bill ID
 * @returns {Promise<Object>} Response with bill details and attachments
 */
export const fetchBillDetails = async (billId) => {
  if (!billId) {
    throw new Error('Bill ID is required');
  }

  const url = `${SUPERADMIN_BASE_URL}/bills/${billId}/`;

  try {
    const response = await authenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch bill details');
    }

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching bill details:', error);
    throw new Error(error.message || 'Failed to fetch bill details');
  }
};